import React, { useState } from "react";
import { View, StyleSheet, Platform } from "react-native";
import { useSelector } from "react-redux";
import LhButton from "../../../components/LhButton";
import LHOverlay from "../../../components/LHOverlay";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import { NHOEmployeeData } from "../../../store/slices/nhoSlice";
import {
  fetchTrainingContents,
  resetFetchFlag,
} from "../../../store/slices/employeeProfileSlice";
import { useAppDispatch } from "../../../store";

// Define the DropdownItem interface to match what's in CrossPlatformDropdown
interface DropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface NHOEmployeeSelectorProps {
  isModalVisible: boolean;
  closeModal: () => void;
  onEmployeeSelect: (employeeId: string) => void;
}

const NHOEmployeeSelector: React.FC<NHOEmployeeSelectorProps> = ({
  isModalVisible,
  closeModal,
  onEmployeeSelect,
}) => {
  // Get employees from nho slice instead of appData
  const { employees = [] } = useSelector((state: any) => state.nho);
  const dispatch = useAppDispatch();

  // Local state for selected employee
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [showValidation, setShowValidation] = useState(false);

  // Prepare employee options for dropdown
  const employeeOptions =
    Array.isArray(employees) && employees.length > 0
      ? employees.map((emp: NHOEmployeeData) => ({
          id: emp.ID?.toString() || "", // Use ID from nho slice data
          displayText: `${emp.First_Name || ""} ${emp.Last_Name || ""} (${
            emp.GPID || ""
          })`,
        }))
      : [];

  // Handle view profile button click
  const handleViewProfile = () => {
    if (!selectedEmployee) {
      setShowValidation(true);
      return;
    }

    // Reset the fetch flag to ensure we get fresh data
    dispatch(resetFetchFlag());

    // Fetch training contents for the selected employee
    dispatch(fetchTrainingContents(selectedEmployee));

    // Proceed with employee selection
    onEmployeeSelect(selectedEmployee);
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedEmployee("");
    setShowValidation(false);
    closeModal();
  };

  return (
    <LHOverlay
      visible={isModalVisible}
      onDismiss={handleClose}
      title="Select Employee to View Profile"
      size="small"
    >
      <View style={styles.modalContainer}>
        <View style={styles.filterItem}>
          <CrossPlatformDropdown
            childList={employeeOptions}
            selectedValue={
              selectedEmployee
                ? {
                    id: selectedEmployee,
                    displayText:
                      employeeOptions.find((e) => e.id === selectedEmployee)
                        ?.displayText || "",
                  }
                : null
            }
            setSelectedValue={(value: DropdownItem | DropdownItem[] | null) => {
              // Handle single selection
              if (value && !Array.isArray(value)) {
                setSelectedEmployee(value.id.toString());
                setShowValidation(false);
              }
              // Handle no selection
              else if (value === null) {
                setSelectedEmployee("");
              }
            }}
            placeholder="Select Employee"
            helperText={
              showValidation && !selectedEmployee
                ? "Employee selection is required"
                : ""
            }
            feedbackType={
              showValidation && !selectedEmployee ? "error" : "info"
            }
            testID="employee-selector-dropdown"
          />
        </View>

        <View style={styles.modalButtons}>
          <LhButton
            text="Cancel"
            variant="secondary"
            size="small"
            onPress={handleClose}
            isLoading={false}
            disabled={false}
            style={styles.modalButton}
            testID="employee-selector-cancel"
          />
          <LhButton
            text="View"
            variant="primary"
            size="small"
            onPress={handleViewProfile}
            isLoading={false}
            disabled={false}
            style={styles.modalButton}
            testID="employee-selector-view"
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    padding: 16,
    width: Platform.OS === "web" ? "90%" : "100%",
    alignSelf: "center",
  },
  filterItem: {
    marginBottom: 20,
    width: "100%",
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  modalButton: {
    width: Platform.OS === "web" ? "15%" : "40%",
    fontSize: 16,
  },
});

export default NHOEmployeeSelector;
